import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DefaultButton,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  makeStyles,
  Typography,
  LinearProgress,
} from '@bb-ui/react-library';
import { Stream, Location, Calendar } from '@bb-ui/icons/dist/small';

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    width: '100%',
    maxWidth: '600px',
  },
  noCloseButtonTitle: {
    '& button': {
      display: 'none !important',
    },
    '& .MuiIconButton-root': {
      display: 'none !important',
    },
  },
  dialogContent: {
    padding: theme.spacing(3, 4),
  },
  list: {
    width: '100%',
    marginTop: theme.spacing(1),
  },
  listItem: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      backgroundColor: theme.palette.grey[100],
      cursor: 'pointer',
    },
  },
  progressContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    padding: theme.spacing(2, 0),
    gap: theme.spacing(1),
    textAlign: 'center',
  },
  progressBar: {
    height: 10,
    borderRadius: 5,
    margin: theme.spacing(2, 0),
  },
  progressLabel: {
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  statusText: {
    color: theme.palette.text.secondary,
    fontStyle: 'italic',
  },
}));

interface ReportGenerationOptionsDialogProps {
  open: boolean;
  onClose: () => void;
  onGenerateCurrent: () => void;
  onGenerateGlobal: () => void;
  onGenerateDeclined: () => void;
  currentRegionLabel: string;
  isGenerating: boolean;
  progress: number;
  statusLabel: string;
}

export const ReportGenerationOptionsDialog: React.FC<ReportGenerationOptionsDialogProps> = ({
  open,
  onClose,
  onGenerateCurrent,
  onGenerateGlobal,
  onGenerateDeclined,
  currentRegionLabel,
  isGenerating,
  progress,
  statusLabel,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const handleCurrent = () => {
    onGenerateCurrent();
  };

  const handleGlobal = () => {
    onGenerateGlobal();
  };

  const handleDeclined = () => {
    onGenerateDeclined();
  };

  return (
    <Dialog
      open={open}
      onClose={!isGenerating ? onClose : undefined}
      fullWidth={true}
      maxWidth="sm"
      classes={{ paper: classes.dialogPaper }}
      disableEscapeKeyDown={isGenerating}
    >
      <DialogTitle className={classes.noCloseButtonTitle} closeLabel={t('global.close')}>
        {t('cohorts.reportGenerationOptions.title')}
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        {isGenerating ? (
          <div className={classes.progressContainer}>
            <Typography variant="subtitle1" gutterBottom>
              {t('cohorts.reportGenerationOptions.generating')}
            </Typography>

            <LinearProgress
              className={classes.progressBar}
              variant="determinate"
              value={progress}
              label=""
              id="report-generation-progress-bar"
            />

            <Typography variant="caption" className={classes.progressLabel}>
              {t('cohorts.reportGenerationOptions.progress', { value: progress })}
            </Typography>

            <Typography variant="body2" className={classes.statusText}>
              {statusLabel}
            </Typography>
          </div>
        ) : (
          <>
            <Typography variant="body1" color="textPrimary" paragraph>
              {t('cohorts.reportGenerationOptions.selectScope')}
            </Typography>

            <List className={classes.list}>
              <ListItemButton
                onClick={handleCurrent}
                className={classes.listItem}
                disabled={isGenerating}
              >
                <ListItemIcon>
                  <Location color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>
                      {t('cohorts.reportGenerationOptions.currentRegion', {
                        region: currentRegionLabel,
                      })}
                    </Typography>
                  }
                  secondary={t('cohorts.reportGenerationOptions.currentRegionDesc')}
                />
              </ListItemButton>

              <ListItemButton
                onClick={handleGlobal}
                className={classes.listItem}
                disabled={isGenerating}
              >
                <ListItemIcon>
                  <Stream color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>
                      {t('cohorts.reportGenerationOptions.global')}
                    </Typography>
                  }
                  secondary={t('cohorts.reportGenerationOptions.globalDesc')}
                />
              </ListItemButton>

              <ListItemButton
                onClick={handleDeclined}
                className={classes.listItem}
                disabled={isGenerating}
              >
                <ListItemIcon>
                  <Calendar color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>
                      {t('cohorts.reportGenerationOptions.declined', {
                        region: currentRegionLabel,
                      })}
                    </Typography>
                  }
                  secondary={t('cohorts.reportGenerationOptions.declinedDesc')}
                />
              </ListItemButton>
            </List>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <DefaultButton onClick={onClose} disabled={isGenerating}>
          {isGenerating
            ? t('cohorts.reportGenerationOptions.buttons.pleaseWait')
            : t('cohorts.reportGenerationOptions.buttons.cancel')}
        </DefaultButton>
      </DialogActions>
    </Dialog>
  );
};

export default ReportGenerationOptionsDialog;
